<script lang="ts">
    import Icon from '@iconify/svelte'
    import type { DateTime } from 'luxon'

    import Date from 'hydria/components/Date.svelte'
    import Modal from 'hydria/components/Modal.svelte'
    // import PopEntry from 'hydria/components/PopEntry.svelte'
    import { uuid } from 'hydria/document_helpers'
    import Flatpickr from 'hydria/external/Flatpickr.svelte'
    import { type TGettext, gettext as _, resolve, tgettext } from 'hydria/services/gettext'
    import { onMount } from 'svelte'

    let value: DateTime | null | undefined = $state()

    function load(currentValue: DateTime | undefined | null) {
        if (value !== currentValue) value = currentValue
        // normalize undefined
        if (value == null) value = undefined
    }

    interface Props {
        saved: DateTime | undefined | null
        value: DateTime | undefined | null
        disabled?: boolean
        hidden?: boolean
        withTime?: boolean
        withSeconds?: boolean
        allowClear?: boolean
        label: TGettext
        isValid?: any
        unconfiguredText?: TGettext | string | null
        helpText?: TGettext | null

        save?: (v) => void
    }

    let { saved: savedValue, value: currentValue = $bindable(), disabled = false, hidden = false, withTime = false, withSeconds = false, allowClear = false, label, isValid = (value: DateTime | null) => true, unconfiguredText = null, helpText = null, save = (v) => {} }: Props = $props()

    let isOpen = $state(false)

    function open() {
        if (disabled) return
        value = currentValue == null ? undefined : currentValue
        isOpen = true
    }
    function close() {
        value = currentValue == null ? undefined : currentValue
        isOpen = false
    }

    function keep() {
        currentValue = value
        save(currentValue)
        isOpen = false
    }

    const id = uuid('t')

    const config = {
        enableTime: withTime,
        enableSeconds: withTime && withSeconds,
        time_24hr: true,
        mode: 'single',
        inline: true,
    }
    // let format = 'Y-m-d'
    let format = $state('yyyy-MM-dd')
    if (withTime) {
        if (withSeconds) format += ' HH:mm:ss'
        else format += ' HH:mm'
    }
    onMount(() => {
        load(currentValue)
    })
    let savedIsValid = $derived(isValid(savedValue))
    let isPristine = $derived((currentValue ? currentValue.toMillis() : null) === (savedValue ? savedValue.toMillis() : null))

    let statusIcon: string = $derived(isPristine ? (savedIsValid ? 'mdi:check-circle' : 'mdi:alert') : isValid(currentValue) ? 'mdi:undo' : 'mdi:undo')
    let statusClass: string = $derived(isPristine ? (savedIsValid ? 'btn-link' : 'btn-danger') : isValid(currentValue) ? 'btn-outline-warning' : 'btn-outline-danger')
</script>

<style>
    .gen-datetime-input-modal {
        /* Sortir du flow normal pour ne par ajouter un spacing pendant que le modal est ouvert. */
        position: absolute;
    }
    .gen-datetime-input-modal :global(.modal-body :flatpickr-calendar) {
        margin-left: auto;
        margin-right: auto;
    }
    .gen-datetime-input-modal :global(.modal) {
        --bs-modal-width: 21.375rem !important;
    }
</style>

<svelte:window
    onkeydown={(e) => {
        if (e.key == 'Escape' && isOpen) close()
        // else if (e.key == 'Enter' && isValid(value) && isOpen) keep()
    }}
/>

{#if !hidden}
    <div class="row g-3 align-items-baseline">
        <div class="col-2 text-end"><label aria-hidden="true" for={id} class="col-form-label" onclick={() => open()}>{$resolve(label)}</label></div>
        <div class="col">
            <!-- {#if isOpen}
                <PopEntry close={() => close()}>
                    <div class="card">
                        <div class="card-header text-bg-primary">{$resolve(label)}</div>
                        <div class="card-body">
                            <Flatpickr options={config} bind:value dateFormat={format} element="#{id}" isLuxon={true}>
                                <div {id} class="input-group" style="display: none">
                                    <input type="text" class="form-control" data-input />
                                </div>
                            </Flatpickr>
                            {#if helpText != null}
                                <div class="form-text">{$resolve(helpText)}</div>
                            {/if}
                        </div>
                        <div class="card-footer d-flex gap-2 justify-content-end">
                            {#if allowClear}
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-secondary"
                                    on:click={() => {
                                        value = null
                                        keep()
                                    }}
                                    disabled={!value}>{$_('Vider')}</button
                                >
                            {/if}
                            <button type="button" class="btn btn-sm btn-outline-secondary" on:click={() => close()}>{$_('Fermer')}</button>
                            <button type="button" class="btn btn-sm btn-primary" disabled={!isValid(value)} on:click={() => keep()}>{$_('Appliquer')}</button>
                        </div>
                    </div>
                </PopEntry>
            {/if} -->
            <div aria-hidden="true" onclick={() => open()} class="clickable">
                {#if currentValue}
                    <Date value={currentValue} {format} />
                {:else}
                    <em class="text-muted">{unconfiguredText ? $resolve(unconfiguredText) : $_('non-configuré')}</em>
                {/if}
            </div>
            {#if helpText != null}
                <div class="form-text">{$resolve(helpText)}</div>
            {/if}
        </div>
        <div class="col-auto text-end">
            <button
                type="button"
                class="btn btn-sm {statusClass}"
                disabled={isPristine}
                onclick={() => {
                    value = savedValue
                    keep()
                }}><Icon inline icon={statusIcon} /></button
            >
            <button type="button" class="btn btn-sm btn-outline-secondary" {disabled} onclick={() => open()}><Icon inline icon="mdi:edit" /></button>
        </div>
    </div>
{/if}

{#if isOpen}
    <div class="gen-datetime-input-modal">
        <Modal close={() => close()} keep={() => keep()}>
            <div class="modal-header text-bg-primary">{$resolve(label)}</div>
            <div class="modal-body">
                <Flatpickr options={config} bind:value dateFormat={format} element="#{id}" isLuxon={true}>
                    <div {id} class="input-group" style="display: none">
                        <input type="text" class="form-control" data-input />
                    </div>
                </Flatpickr>
                {#if helpText != null}
                    <div class="form-text">{$resolve(helpText)}</div>
                {/if}
            </div>
            <div class="modal-footer">
                {#if allowClear}
                    <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary"
                        onclick={() => {
                            value = null
                            keep()
                        }}
                        disabled={!value}>{$_('Vider')}</button
                    >
                {/if}
                <button type="button" class="btn btn-sm btn-outline-secondary" onclick={() => close()}>{$_('Fermer')}</button>
                <button type="button" class="btn btn-sm btn-primary" disabled={!isValid(value) || value === currentValue} onclick={() => keep()}>{$_('Appliquer')}</button>
            </div>
        </Modal>
    </div>
{/if}
